import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Chip,
  Link,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { MenuItemResponse } from '../../types/api'
import { getIconForTela } from '../../utils/iconMapper'

interface TelaTreeViewProps {
  telas: MenuItemResponse[]
  onTelaClick?: (tela: MenuItemResponse) => void
}

const TelaTreeItem: React.FC<{
  tela: MenuItemResponse
  depth?: number
  onTelaClick?: (tela: MenuItemResponse) => void
}> = ({ tela, depth = 0, onTelaClick }) => {
  const [open, setOpen] = useState(true)
  const hasChildren = tela.children && tela.children.length > 0
  const icon = getIconForTela(tela.nome)

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          pl: `${depth * 2 + 1}rem`,
        }}
      >
        <ListItemButton
          onClick={() => {
            if (hasChildren) {
              setOpen(!open)
            } else if (onTelaClick) {
              onTelaClick(tela)
            }
          }}
          sx={{
            py: 0.5,
            minHeight: 40,
          }}
        >
          {hasChildren && (
            <ListItemIcon sx={{ minWidth: 32 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  setOpen(!open)
                }}
                sx={{ p: 0.5 }}
              >
                {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
            </ListItemIcon>
          )}
          {!hasChildren && <ListItemIcon sx={{ minWidth: 32 }} />}
          {icon && (
            <ListItemIcon sx={{ minWidth: 32 }}>
              {icon}
            </ListItemIcon>
          )}
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">{tela.nome}</Typography>
                <Chip
                  label={tela.tipo_sistema}
                  size="small"
                  color={tela.tipo_sistema === 'ADMIN' ? 'primary' : 'secondary'}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                    {tela.abrir_nova_aba && (
                      <Chip
                        label="Nova aba"
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                {tela.rota && (
                  <Link
                    href={tela.rota}
                        target={tela.abrir_nova_aba ? '_blank' : '_self'}
                        rel={tela.abrir_nova_aba ? 'noopener noreferrer' : undefined}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (onTelaClick) {
                        onTelaClick(tela)
                      }
                    }}
                    sx={{
                      ml: 'auto',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {tela.rota}
                  </Link>
                )}
              </Box>
            }
          />
        </ListItemButton>
      </ListItem>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {tela.children!.map((child) => (
              <TelaTreeItem
                key={child.id}
                tela={child}
                depth={depth + 1}
                onTelaClick={onTelaClick}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  )
}

const TelaTreeView: React.FC<TelaTreeViewProps> = ({ telas, onTelaClick }) => {
  if (!telas || telas.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Nenhuma tela encontrada
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 2, maxHeight: '70vh', overflowY: 'auto' }}>
      <List>
        {telas.map((tela) => (
          <TelaTreeItem
            key={tela.id}
            tela={tela}
            onTelaClick={onTelaClick}
          />
        ))}
      </List>
    </Paper>
  )
}

export default TelaTreeView

